// src/pages/QuestionComparison.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { useAuth } from '../components/AuthContext';
import Layout from '../components/Layout';
import Login from '../components/Login';
import './QuestionComparison.css';

function QuestionComparison() {
    const [question, setQuestion] = useState(null);
    const [videos, setVideos] = useState([]);
    const [questionText, setQuestionText] = useState('');
    const [currentVideoUrl, setCurrentVideoUrl] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentVideoName, setCurrentVideoName] = useState('');
    const [currentComparisonIndex, setCurrentComparisonIndex] = useState(-1);
    const playerRef = useRef(null);
    // Only gets set to true from the handlePlayerReady callback. It never gets set to false afterwards.
    const [playerReady, setPlayerReady] = useState(false); 

    // Used to detect if the user has clicked on the button for the video that is already playing.
    // Needed to work around some ReactPlayer weirdness.
    const [restartVideo, setRestartVideo] = useState(false);
    const { currentUser, setAuthError } = useAuth();
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:5000/api';
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const fetchVideosAndSetInitialVideo = async () => {
            if (currentUser) {
                try {
                    const idToken = await currentUser.getIdToken();
                    const questionId = searchParams.get('questionId');
                    const questionResponse = await axios.get(`${backendUrl}/questions/${questionId}`, {
                        headers: {
                            'Authorization': `Bearer ${idToken}`
                        }
                    });
                    setQuestion(questionResponse.data);
                    setQuestionText(questionResponse.data.question_text);
                    const allDataResponse = await axios.get(`${backendUrl}/all-data`, {
                        headers: {
                            'Authorization': `Bearer ${idToken}`
                        }
                    });
                    setVideos(allDataResponse.data);
                    setAuthError(null);
                    const firstVideo = allDataResponse.data.find(video => video.id === questionResponse.data.interview_ids[0]);

                    if (firstVideo) {
                        setCurrentVideoName(firstVideo.name);
                        setCurrentVideoUrl(firstVideo.url);
                        setCurrentComparisonIndex(0);
                        const foundQuestion = firstVideo.questions.find(q => q.question_text === questionResponse.data.question_text);
                        // When the page loads for the firsr time, we start playing the video automatically at the point where the
                        // QUESTION is asked, not where the answer starts.
                        setCurrentTime(foundQuestion?.question_start || 0);
                    }
                } catch (error) {
                    if (error.response && error.response.status === 403 && error.response.data.error === 'Unauthorized Access') {
                        // This is our specific backend authorization error
                        setAuthError(error.response.data.message);
                    } else {
                        console.error("Error fetching data:", error);
                        setAuthError("An unexpected error occurred while fetching data.");
                    }

                }
            } else {
                setAuthError(null); // Clear error if not logged in
                setVideos([]); // Clear videos if logged out
                setQuestion(null);
            }
        };

        fetchVideosAndSetInitialVideo();
    }, [currentUser, searchParams]);

    const handlePlayerReady = () => {        
        setPlayerReady(true);
    };

    const handleVideoChange = (videoId, videoName, answerStart, index) => {
        const selectedVideo = videos.find(v => v.id === videoId);
        if (selectedVideo && selectedVideo.url) {
            if (videoName === currentVideoName) {
                // We're being asked to restart the currently playing video.
                // There's an edge case here though because when the page first loads, we
                // start playing the first video at the point where the question is asked
                // rather than when the answer starts. So, if we are restarting the same video,
                // set the market for restart AND set the current time to the start of the answer.
                setRestartVideo(true);
                setCurrentTime(answerStart);
            } else {
                setCurrentVideoUrl(selectedVideo.url);
                setCurrentTime(answerStart);
                setCurrentVideoName(videoName);
                setCurrentComparisonIndex(index);
                setRestartVideo(false);
            }
        } else {
            if (selectedVideo) {
                console.warn(JSON.stringify(selectedVideo));
            }
        }
    };

    const handlePrevNextVideo = (direction) => {
        let newIndex = currentComparisonIndex;
        if (direction === 'prev') {
            newIndex = Math.max(0, currentComparisonIndex - 1);
        } else if (direction === 'next') {
            newIndex = Math.min(question.interview_ids.length - 1, currentComparisonIndex + 1);
        }
        if (newIndex !== currentComparisonIndex && question.interview_ids[newIndex]) {
            const foundVideo = videos.find(v => v.id === question.interview_ids[newIndex]);
            const foundQuestion = foundVideo.questions.find(q => q.question_text === questionText);
            handleVideoChange(foundVideo.id, foundVideo.name, foundQuestion.answer_start, newIndex);
        }
    };

    useEffect(() => {
        if (playerRef.current && currentTime > 0 && playerReady) {
            playerRef.current.seekTo(currentTime);
            if (!restartVideo) {
                playerRef.current.getInternalPlayer().play();
            } else {
                setRestartVideo(false);
            }
        }
    }, [playerReady, currentTime, currentVideoUrl, restartVideo]);


    if (!currentUser) {
        return (
            <div className="comparison-container">
                <Login className="login-button" />
            </div>
        );
    }

    return (
        <Layout>
            <div className="comparison-container">
                <h1>{questionText}</h1>
                {currentVideoName && <h3 className="current-video-name">{currentVideoName}</h3>}
                <div className="single-video-player">
                    {currentVideoUrl && (
                        <ReactPlayer
                            ref={playerRef}
                            url={currentVideoUrl}
                            controls
                            width="80%"
                            height="auto"
                            className="video-player"
                            onReady={handlePlayerReady}
                        />
                    )}
                </div>
                <div className="video-navigation-buttons">
                    <button
                        onClick={() => handlePrevNextVideo('prev')}
                        className="nav-button"
                        disabled={currentComparisonIndex <= 0}
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => handlePrevNextVideo('next')}
                        className="nav-button"
                        disabled={currentComparisonIndex >= (question ? question.interview_ids.length - 1 : true)}
                    >
                        Next
                    </button>
                </div>
                <div className="video-selector">
                    <ul>
                        {question && question.interview_ids.map((interviewId, index) => {
                            const foundVideo = videos.find(v => v.id === interviewId);
                            if (foundVideo) {
                                const foundQuestion = foundVideo.questions.find(q => q.question_text === questionText);
                                return (
                                    foundQuestion && (
                                        <li key={interviewId} className="video-card">
                                            <button onClick={() => handleVideoChange(interviewId, foundVideo.name, foundQuestion.answer_start, index)} className="select-video-button">
                                                {foundVideo.name}
                                            </button>
                                        </li>
                                    )
                                );
                            } else {
                                return null;
                            }
                        })}
                    </ul>
                </div>
            </div>
        </Layout>
    );
}

export default QuestionComparison;